import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import DeviceTable from './DeviceTable';
import FirmwareUpdateModal from './FirmwareUpdateModal';
import DeviceAlertModal from './DeviceAlertModal';
import { Device } from "../types";
import ModelCheckboxFilter from './ModelCheckboxFilter';
import { getLoggingModeErrorMessage, updateDeviceLoggingMode } from '../utils/loggingMode';
import { fieldsForOtaMode, OtaMode } from '../utils/otaMode';

interface AllDevicesProps {
  apiBaseUrl: string;
  onDeviceUpdate: () => void;
  canInstallFirmware?: boolean;
}

type DeviceDateFilterField = 'recordCreated' | 'factory.flashed' | 'lastCheckIn' | 'lastUpdated';

const AllDevices: React.FC<AllDevicesProps> = ({
  apiBaseUrl,
  onDeviceUpdate,
  canInstallFirmware = true,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [devices, setDevices] = useState<Device[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loadingInitial, setLoadingInitial] = useState<boolean>(true);
  const [updatingOTA, setUpdatingOTA] = useState<string | null>(null);
  const [totalDevices, setTotalDevices] = useState<number>(0);
  const [firmwareUpdateDevice, setFirmwareUpdateDevice] = useState<Device | null>(null);
  const [alertDevice, setAlertDevice] = useState<Device | null>(null);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [limit] = useState<number>(10);
  
  // Sorting states
  const [sortColumn, setSortColumn] = useState<string>('model');
  const [sortDirection, setSortDirection] = useState<string>('asc');
  
  const parseModelsFromParams = (params: URLSearchParams) =>
    params.get('model')?.split(',').map((model) => model.trim()).filter(Boolean) || [];

  const toDateTimeLocalValue = (date: Date) => {
    const offsetDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return offsetDate.toISOString().slice(0, 16);
  };

  const clampFutureDateTimeLocal = (value: string) => {
    if (!value) return '';
    const selectedDate = new Date(value);
    if (Number.isNaN(selectedDate.getTime())) return '';

    const now = new Date();
    return selectedDate > now ? toDateTimeLocalValue(now) : value;
  };

  // Search state
  const [searchSerial, setSearchSerial] = useState<string>(searchParams.get('serial') || '');
  const [searchVersion, setSearchVersion] = useState<string>(searchParams.get('version') || '');
  const [selectedModels, setSelectedModels] = useState<string[]>(parseModelsFromParams(searchParams));
  const [searchType, setSearchType] = useState<string>(searchParams.get('type') || '');
  const [searchGroup, setSearchGroup] = useState<string>(searchParams.get('group') || '');
  const [dateField, setDateField] = useState<string>(searchParams.get('dateField') || '');
  const [dateStart, setDateStart] = useState<string>(searchParams.get('dateStart') || '');
  const [dateEnd, setDateEnd] = useState<string>(searchParams.get('dateEnd') || '');
  const DEVICE_TYPES: { value: Device['deployment']['type']; label: string }[] = [
    { value: 'prod', label: 'Production' },
    { value: 'beta', label: 'Beta' },
    { value: 'dev', label: 'Development' },
    { value: 'deco', label: 'Decommissioned' },
  ];

  const DATE_FILTER_FIELDS: { value: DeviceDateFilterField; label: string }[] = [
    { value: 'recordCreated', label: 'Record Created' },
    { value: 'factory.flashed', label: 'Factory Flashed' },
    { value: 'lastCheckIn', label: 'Last Check-In' },
    { value: 'lastUpdated', label: 'Last Updated' },
  ];

  const alphabet = Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i));

  const fetchDevices = useCallback(async (page: number) => {
    setLoadingInitial(true);
    try {
      const params: Record<string, string | number> = {
        page,
        limit,
        sortColumn,
        sortDirection
      };

      const serial = searchParams.get('serial');
      const version = searchParams.get('version');
      const model = searchParams.get('model');
      const type = searchParams.get('type');
      const group = searchParams.get('group');
      const queryDateField = searchParams.get('dateField');
      const queryDateStart = searchParams.get('dateStart');
      const queryDateEnd = searchParams.get('dateEnd');

      if (serial) params.serial = serial;
      if (version) params.version = version;
      if (model) params.model = model;
      if (type) params.type = type;
      if (group) params.group = group;
      if (queryDateField) params.dateField = queryDateField;
      if (queryDateStart) params.dateStart = queryDateStart;
      if (queryDateEnd) params.dateEnd = queryDateEnd;
      if (queryDateField && (queryDateStart || queryDateEnd)) {
        params.dateTimezoneOffset = new Date().getTimezoneOffset();
      }

      const response = await axios.get(`${apiBaseUrl}/devices`, {
        withCredentials: true,
        params
      });

      setDevices(response.data.devices);
      setTotalPages(response.data.totalPages);
      setCurrentPage(page);
      setTotalDevices(response.data.totalDevices);
      setError(response.data.totalDevices === 0 && response.data.hasFilters
        ? 'No devices found matching the current filters'
        : null
      );
    } catch (error: any) {
      setError('Failed to fetch devices');
    } finally {
      setLoadingInitial(false);
    }
  }, [apiBaseUrl, limit, searchParams, sortColumn, sortDirection]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();

    const nextParams = new URLSearchParams();
    if (searchSerial.trim()) nextParams.set('serial', searchSerial.trim());
    if (searchVersion.trim()) nextParams.set('version', searchVersion.trim());
    if (selectedModels.length > 0) nextParams.set('model', selectedModels.join(','));
    if (searchType) nextParams.set('type', searchType);
    if (searchGroup) nextParams.set('group', searchGroup);
    if (dateField && (dateStart || dateEnd)) {
      nextParams.set('dateField', dateField);
      if (dateStart) nextParams.set('dateStart', clampFutureDateTimeLocal(dateStart));
      if (dateEnd) nextParams.set('dateEnd', clampFutureDateTimeLocal(dateEnd));
    }

    setSearchParams(nextParams);
  };

  const handleSortChange = (column: string) => {
    if (column === sortColumn) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const handleClearSearch = () => {
    setSearchSerial('');
    setSearchVersion('');
    setSelectedModels([]);
    setSearchType('');
    setSearchGroup('');
    setDateField('');
    setDateStart('');
    setDateEnd('');
    setSearchParams({});
  };

  const maxDateTimeLocal = toDateTimeLocalValue(new Date());

  useEffect(() => {
    setSearchSerial(searchParams.get('serial') || '');
    setSearchVersion(searchParams.get('version') || '');
    setSelectedModels(parseModelsFromParams(searchParams));
    setSearchType(searchParams.get('type') || '');
    setSearchGroup(searchParams.get('group') || '');
    setDateField(searchParams.get('dateField') || '');
    setDateStart(searchParams.get('dateStart') || '');
    setDateEnd(searchParams.get('dateEnd') || '');
    fetchDevices(1);
  }, [searchParams, sortColumn, sortDirection, fetchDevices]);

  const handleToggleMarked = async (serial: string, value: OtaMode) => {
    try {
      setUpdatingOTA(serial);
      
      // Update the server
      await axios.patch(
        `${apiBaseUrl}/devices/${serial}`,
        { deployment: { otaMode: value } },
        { withCredentials: true }
      );
      
      // Then update local state
      setDevices((prev) =>
        prev.map((device) =>
          device.serial === serial
            ? { ...device, deployment: { ...device.deployment, ...fieldsForOtaMode(value) } }
            : device
        )
      );
      
      // Notify parent component about the update
      onDeviceUpdate();
      
      setError(null);
    } catch (error: any) {
      console.error('Error updating device OTA status:', error);
      setError(`Failed to update OTA status: ${error.response?.data?.message || error.message}`);
      
      fetchDevices(currentPage);
    } finally {
      setUpdatingOTA(null);
    }
  };

  const handleUpdateLoggingMode = async (serial: string, mode: number) => {
    try {
      const { device } = await updateDeviceLoggingMode(apiBaseUrl, serial, mode);

      setDevices((prev) =>
        prev.map((entry) =>
          entry.serial === serial ? { ...entry, ...device, loggingMode: mode } : entry
        )
      );

      onDeviceUpdate();
      setError(null);
    } catch (error: unknown) {
      console.error('Error updating device logging mode:', error);
      setError(`Failed to update logging mode: ${getLoggingModeErrorMessage(error)}`);
      fetchDevices(currentPage);
    }
  };

  return (
    <div className="mb-5">
      
      {/* Search Row */}
      <div className="responsive-filter-bar d-flex flex-nowrap align-items-center gap-2 mb-3">
        <form onSubmit={handleSearch} className="d-flex flex-column gap-2 flex-grow-1 min-w-0">
          <div className="d-flex flex-nowrap align-items-center gap-2 w-100">
            <input
              type="text"
              className="form-control form-control-sm flex-shrink-0"
              placeholder="Serial"
              value={searchSerial}
              onChange={(e) => setSearchSerial(e.target.value)}
              style={{ width: '90px' }}
            />
            <input
              type="text"
              className="form-control form-control-sm flex-shrink-0"
              placeholder="Version"
              value={searchVersion}
              onChange={(e) => setSearchVersion(e.target.value)}
              style={{ width: '80px' }}
            />
            <ModelCheckboxFilter
              apiBaseUrl={apiBaseUrl}
              selectedModels={selectedModels}
              onChange={setSelectedModels}
            />
            <div className="input-group input-group-sm flex-shrink-0" style={{ width: 'auto' }}>
              <span className="input-group-text">Deployment</span>
              <select
                className="form-select"
                value={searchType}
                onChange={(e) => setSearchType(e.target.value)}
                aria-label="Filter by deployment"
                style={{ width: '100px' }}
              >
                <option value="">All</option>
                {DEVICE_TYPES.map(({ value, label }) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
            <div className="input-group input-group-sm flex-shrink-0" style={{ width: 'auto' }}>
              <span className="input-group-text">Group</span>
              <select
                className="form-select"
                value={searchGroup}
                onChange={(e) => setSearchGroup(e.target.value)}
                aria-label="Filter by group"
                style={{ width: '84px' }}
              >
                <option value="">All</option>
                {alphabet.map((letter) => (
                  <option key={letter} value={letter}>{letter}</option>
                ))}
              </select>
            </div>
            <div className="ms-auto d-flex align-items-center gap-2 flex-shrink-0">
              <button type="button" className="btn btn-outline-secondary btn-sm flex-shrink-0" onClick={handleClearSearch}>
                Clear
              </button>
              <button type="submit" className="btn btn-primary btn-sm flex-shrink-0">Search</button>
            </div>
          </div>

          <div className="d-flex flex-nowrap align-items-center gap-2 w-100">
            <div className="input-group input-group-sm flex-shrink-0" style={{ width: 'auto' }}>
              <span className="input-group-text">Date</span>
              <select
                className="form-select"
                value={dateField}
                onChange={(e) => setDateField(e.target.value)}
                aria-label="Filter by date field"
                style={{ width: '180px' }}
              >
                <option value="">None</option>
                {DATE_FILTER_FIELDS.map(({ value, label }) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
            <input
              type="datetime-local"
              className="form-control form-control-sm flex-shrink-0"
              value={dateStart}
              onChange={(e) => setDateStart(e.target.value)}
              aria-label="Start date and time"
              title="Start date and time"
              disabled={!dateField}
              max={maxDateTimeLocal}
              style={{ width: '230px' }}
            />
            <input
              type="datetime-local"
              className="form-control form-control-sm flex-shrink-0"
              value={dateEnd}
              onChange={(e) => setDateEnd(e.target.value)}
              aria-label="End date and time"
              title="End date and time"
              disabled={!dateField}
              max={maxDateTimeLocal}
              style={{ width: '230px' }}
            />
          </div>
        </form>
      </div>
      
      <DeviceTable
        devices={devices}
        loading={loadingInitial}
        error={error}
        onToggleMarked={handleToggleMarked}
        onUpdateLoggingMode={handleUpdateLoggingMode}
        onFirmwareInstall={canInstallFirmware ? setFirmwareUpdateDevice : undefined}
        onAlertsOpen={setAlertDevice}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onSortChange={handleSortChange}
        currentPage={currentPage}
        totalPages={totalPages}
        totalDevices={totalDevices}
        onPageChange={fetchDevices}
        showPagination={totalPages > 0}
        updatingOTA={updatingOTA}
      />

      <FirmwareUpdateModal
        apiBaseUrl={apiBaseUrl}
        device={firmwareUpdateDevice}
        isOpen={firmwareUpdateDevice !== null}
        onClose={() => setFirmwareUpdateDevice(null)}
      />

      <DeviceAlertModal
        apiBaseUrl={apiBaseUrl}
        device={alertDevice}
        isOpen={alertDevice !== null}
        onClose={() => setAlertDevice(null)}
      />
    </div>
  );
};

export default AllDevices;